import { useState, useMemo } from 'react';
import { StatusBadge } from '@/components/base/StatCard';
import { blockedCustomers, type BlockedCustomer, type RiskType, type BlockedStatus } from '@/mocks/blocked_customers';
import { useNavigate } from 'react-router-dom';

const riskTypes: RiskType[] = ["Dikkat", "Sorunlu", "Yasaklı", "Etik Dışı Talep"];
const riskColors: Record<RiskType, string> = {
  "Dikkat": "#F59E0B",
  "Sorunlu": "#EF4444",
  "Yasaklı": "#1A1A1A",
  "Etik Dışı Talep": "#8B5CF6",
};

export default function GuvenlikPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [blockedList, setBlockedList] = useState<BlockedCustomer[]>(blockedCustomers);
  const [detailItem, setDetailItem] = useState<BlockedCustomer | null>(null);
  const [newNote, setNewNote] = useState('');
  const [noteModalOpen, setNoteModalOpen] = useState(false);

  const filtered = useMemo(() => {
    let data = [...blockedList];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(b => b.customerName.toLowerCase().includes(q) || b.phone.toLowerCase().includes(q));
    }
    if (riskFilter !== 'all') data = data.filter(b => b.riskType === riskFilter);
    if (statusFilter !== 'all') data = data.filter(b => b.status === statusFilter);
    return data;
  }, [blockedList, searchQuery, riskFilter, statusFilter]);

  const handleRemoveBlock = (id: string) => {
    if (confirm('Bu müşterinin engel durumunu kaldırmak istediğinize emin misiniz?')) {
      setBlockedList(prev => prev.map(b => b.id === id ? { ...b, status: 'Pasif' as BlockedStatus } : b));
    }
  };

  const handleAddNote = () => {
    if (!detailItem || !newNote.trim()) return;
    setBlockedList(prev => prev.map(b => b.id === detailItem.id ? { ...b, staffNote: b.staffNote + `\n\n[${new Date().toLocaleDateString('tr-TR')}] ${newNote}` } : b));
    setNoteModalOpen(false);
    setNewNote('');
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>Güvenlik & Engel Listesi</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{filtered.length} kayıtlı uyarı</p>
        </div>
        <button className="spa-btn-primary text-sm flex items-center gap-2 self-start sm:self-auto">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
          Yeni Uyarı
        </button>
      </div>

      {/* Alert Banner */}
      <div className="flex flex-wrap gap-2">
        {blockedList.filter(b => b.status === 'Aktif').map(b => (
          <div key={b.id} className="flex items-center gap-2 px-3 py-2 rounded-spa-sm text-xs font-medium" style={{ background: riskColors[b.riskType] + '10', color: riskColors[b.riskType], border: `1px solid ${riskColors[b.riskType]}20` }}>
            <i className="ri-shield-keyhole-line" /> {b.customerName} - {b.riskType}
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} /></div>
          <input className="spa-input pl-10" placeholder="Müşteri adı veya telefon ara..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select className="spa-input text-sm min-w-[120px]" value={riskFilter} onChange={e => setRiskFilter(e.target.value)}>
            <option value="all">Tüm Riskler</option>
            {riskTypes.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className="spa-input text-sm min-w-[100px]" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">Tüm Durumlar</option>
            <option value="Aktif">Aktif</option>
            <option value="Pasif">Pasif</option>
            <option value="İzlemede">İzlemede</option>
          </select>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 gap-3">
        {filtered.map(item => (
          <div key={item.id} className="spa-card p-4 space-y-3" style={{ borderLeft: `3px solid ${riskColors[item.riskType]}` }}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0" style={{ background: riskColors[item.riskType] }}>
                  <i className="ri-shield-keyhole-line" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{item.customerName}</p>
                    <span className="spa-badge text-[10px]" style={{ background: riskColors[item.riskType] + '15', color: riskColors[item.riskType] }}>{item.riskType}</span>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.phone}</p>
                </div>
              </div>
              <StatusBadge status={item.status}>{item.status}</StatusBadge>
            </div>

            <div className="p-3 rounded-spa-sm" style={{ background: riskColors[item.riskType] + '06', border: `1px solid ${riskColors[item.riskType]}15` }}>
              <p className="text-xs font-medium mb-1" style={{ color: riskColors[item.riskType] }}><i className="ri-error-warning-line mr-1" />Açıklama</p>
              <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{item.description}</p>
            </div>

            <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
              <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}><i className="ri-user-line mr-1" />Personel Notu</p>
              <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{item.staffNote}</p>
            </div>

            <div className="flex items-center justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
              <span>Kaydı oluşturan: {item.createdBy}</span>
              <span>Son işlem: {item.lastActionDate}</span>
            </div>

            <div className="flex gap-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button onClick={() => setDetailItem(item)} className="spa-btn-secondary text-xs py-2 px-3">
                <i className="ri-eye-line mr-1" />Detay
              </button>
              <button onClick={() => navigate(`/musteriler`)} className="spa-btn-secondary text-xs py-2 px-3">
                <i className="ri-user-line mr-1" />Müşteri Kartı
              </button>
              {item.status === 'Aktif' && (
                <button onClick={() => handleRemoveBlock(item.id)} className="px-3 py-2 rounded-spa-sm text-xs font-medium cursor-pointer" style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }}>
                  <i className="ri-shield-check-line mr-1" />Engeli Kaldır
                </button>
              )}
              <button onClick={() => { setDetailItem(item); setNoteModalOpen(true); }} className="px-3 py-2 rounded-spa-sm text-xs font-medium cursor-pointer" style={{ background: 'rgba(201,169,98,0.12)', color: '#C9A962' }}>
                <i className="ri-add-line mr-1" />Not Ekle
              </button>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 space-y-3">
          <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
            <i className="ri-shield-check-line text-xl" style={{ color: 'var(--text-muted)' }} />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Güvenlik kaydı bulunamadı</p>
        </div>
      )}

      {/* Detail Modal */}
      {detailItem && !noteModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0" style={{ background: 'var(--modal-overlay)' }} onClick={() => setDetailItem(null)} />
          <div className="relative bg-theme-card rounded-spa w-full max-w-md flex flex-col max-h-[90vh] animate-scale-in border overflow-hidden" style={{ borderColor: 'var(--card-border)' }}>
            <div className="flex items-center justify-between p-5 flex-shrink-0">
              <h3 className="text-lg font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Güvenlik Detayı</h3>
              <button onClick={() => setDetailItem(null)} className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="px-5 pb-5 space-y-3 overflow-y-auto flex-1 min-h-0">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg text-white" style={{ background: riskColors[detailItem.riskType] }}>
                  <i className="ri-shield-keyhole-line" />
                </div>
                <div>
                  <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{detailItem.customerName}</p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{detailItem.phone}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Risk Tipi</p><p style={{ color: riskColors[detailItem.riskType] }}>{detailItem.riskType}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Durum</p><StatusBadge status={detailItem.status}>{detailItem.status}</StatusBadge></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Kaydı Oluşturan</p><p style={{ color: 'var(--text-primary)' }}>{detailItem.createdBy}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Son İşlem</p><p style={{ color: 'var(--text-primary)' }}>{detailItem.lastActionDate}</p></div>
              </div>
              <div className="p-3 rounded-spa-sm" style={{ background: riskColors[detailItem.riskType] + '06', border: `1px solid ${riskColors[detailItem.riskType]}15` }}>
                <p className="text-xs font-medium mb-1" style={{ color: riskColors[detailItem.riskType] }}>Açıklama</p>
                <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{detailItem.description}</p>
              </div>
              <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Personel Notu</p>
                <p className="text-sm whitespace-pre-line" style={{ color: 'var(--text-primary)' }}>{detailItem.staffNote}</p>
              </div>
              {detailItem.appointmentHistory.length > 0 && (
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-muted)' }}>Randevu Geçmişi</p>
                  {detailItem.appointmentHistory.map((h, i) => (
                    <p key={i} className="text-sm" style={{ color: 'var(--text-primary)' }}>{h}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {noteModalOpen && detailItem && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0" style={{ background: 'var(--modal-overlay)' }} onClick={() => setNoteModalOpen(false)} />
          <div className="relative bg-theme-card rounded-spa w-full max-w-sm animate-scale-in border p-5 space-y-4" style={{ borderColor: 'var(--card-border)' }}>
            <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Yeni Güvenlik Notu</h3>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{detailItem.customerName} için not ekle</p>
            <textarea className="spa-input min-h-[100px] resize-none" maxLength={500} value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Güvenlik notu yazın..." />
            <div className="flex gap-2">
              <button onClick={() => setNoteModalOpen(false)} className="flex-1 spa-btn-secondary text-sm">İptal</button>
              <button onClick={handleAddNote} className="flex-1 spa-btn-primary text-sm">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}